import React from 'react';
import { CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { PracticeBadge } from '../show/PracticeBadge';
import { isSameDay } from '../../utils/sameDay';

export const ClassSelector = ({
  classesByDay, dayKeys, isMultiDay, activeDayTab, selectedClasses,
  entryCounts, rolloverMap, selectedRollovers, riderAgeGroup,
  practiceCounts = {}, onSetPracticeCount,
  onSetActiveDayTab, onToggleClass, onToggleRollover, getEligibleRollovers
}) => {
  const currentDay = isMultiDay ? (activeDayTab || dayKeys[0]) : dayKeys[0];
  const dayClasses = classesByDay[currentDay] || [];

  return (
    <div className="lg:col-span-2">
      <h2 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Select Classes</h2>

      {isMultiDay && (
        <div className="flex gap-1 mb-4 bg-[#161616] rounded-lg p-1 overflow-x-auto" data-testid="day-tabs">
          {dayKeys.map((day) => {
            const isActive = (activeDayTab || dayKeys[0]) === day;
            const selectedInDay = classesByDay[day].filter(c => selectedClasses.includes(c.id)).length;
            return (
              <button
                key={day}
                onClick={() => onSetActiveDayTab(day)}
                data-testid={`day-tab-${day}`}
                className={`relative flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                  isActive
                    ? 'bg-[#0096FF] text-white'
                    : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                }`}
              >
                {day}
                {selectedInDay > 0 && (
                  <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                    isActive ? 'bg-white/25 text-white' : 'bg-[#0096FF]/20 text-[#0096FF]'
                  }`}>{selectedInDay}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-2" data-testid="class-list">
        {dayClasses.map((cls) => {
          const isSelected = selectedClasses.includes(cls.id);
          const entryCount = entryCounts[cls.id] || 0;
          const availableRollovers = getEligibleRollovers(cls.id);
          // Destination-hint (new "rollover TO" UX). For this SOURCE class,
          // find every class that accepts rollovers FROM it — that's where
          // rider can additionally roll into by entering this class. Shown
          // as a small teaser below the title whether selected or not so
          // riders know the option exists before expanding.
          //
          // v264 — Only tease destinations on the SAME day as the source
          // class. Riders entering Saturday classes shouldn't see Sunday
          // rollover options offered (matches the /rollover-map filter).
          const rolloverDestinations = [];
          for (const day of Object.keys(classesByDay)) {
            for (const d of classesByDay[day] || []) {
              if (d.id === cls.id) continue;
              const sources = d.rollover_from_class_ids?.length
                ? d.rollover_from_class_ids
                : (d.rollover_from_class_id ? [d.rollover_from_class_id] : []);
              if (sources.includes(cls.id) && isSameDay(cls, d)) rolloverDestinations.push(d.name);
            }
          }

          return (
            <div key={cls.id}>
              <div
                onClick={() => {
                  // Block selection when class is at cap. Backend enforces too,
                  // but blocking here gives instant feedback rather than a 409
                  // surfacing only at submit time.
                  const cap = parseInt(cls.entry_cap || 0, 10) || 0;
                  if (cap > 0 && entryCount >= cap && !isSelected) return;
                  onToggleClass(cls.id);
                }}
                data-testid={`class-select-${cls.id}`}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  (() => {
                    const cap = parseInt(cls.entry_cap || 0, 10) || 0;
                    const isFull = cap > 0 && entryCount >= cap && !isSelected;
                    if (isFull) return 'bg-[#161616]/60 border-[#FF3B30]/40 cursor-not-allowed opacity-60';
                    if (isSelected) return 'bg-[#0096FF]/10 border-[#0096FF] cursor-pointer';
                    return 'bg-[#161616] border-[#262626] hover:border-[#0096FF]/50 cursor-pointer';
                  })()
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-[#0096FF] border-[#0096FF]' : 'border-[#555]'
                    }`}>
                      {isSelected && <CheckCircle size={12} weight="fill" className="text-white" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-semibold text-sm">{cls.name}</p>
                        <PracticeBadge classType={cls.class_type} />
                        {cls.is_finals && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FACC15]/20 text-[#FACC15]" data-testid={`class-finals-badge-${cls.id}`}>
                            ★ FINALS
                          </span>
                        )}
                        {(cls.multi_go_group || '').trim() && !cls.is_finals && cls.go_number && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FF9500]/15 text-[#FF9500]" data-testid={`class-multigo-badge-${cls.id}`}
                            title={`${cls.multi_go_group} — Go ${cls.go_number}. One entry fee covers every Go in this group.`}>
                            {cls.go_number === 1 ? '1st Go' : cls.go_number === 2 ? '2nd Go' : cls.go_number === 3 ? '3rd Go' : `Go ${cls.go_number}`}
                          </span>
                        )}
                        {(() => {
                          const cap = parseInt(cls.entry_cap || 0, 10) || 0;
                          if (cap <= 0) return null;
                          const isFull = entryCount >= cap;
                          return (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                isFull
                                  ? 'bg-[#FF3B30]/20 text-[#FF3B30]'
                                  : 'bg-[#FACC15]/15 text-[#FACC15]'
                              }`}
                              data-testid={`class-cap-badge-${cls.id}`}
                            >
                              {isFull ? 'FULL' : `${entryCount}/${cap}`}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-[#9E9E9E] text-xs">
                        {cls.arena && <span className="text-[#0096FF]">{cls.arena} &bull; </span>}
                        {entryCount} entered
                        {cls.division_type && cls.division_type !== 'NONE' && ` \u2022 ${cls.division_type}`}
                      </p>
                      {rolloverDestinations.length > 0 && (
                        <p className="text-[#FF9500] text-[11px] mt-0.5 flex items-center gap-1" data-testid={`rollover-to-hint-${cls.id}`}>
                          <ArrowsClockwise size={10} weight="bold" />
                          Can roll over to: {rolloverDestinations.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-[#34C759] font-bold text-sm" style={{fontFamily: 'JetBrains Mono'}}>${cls.entry_fee}</span>
                </div>
              </div>

              {/* Practice multi-runs — riders entering a warm-up / exhibition
                  can pick the number of runs in one click. Total fee scales
                  on the right so they see the cost preview live. */}
              {isSelected && ['warmup','exhibition'].includes(cls.class_type) && (
                <div
                  className="ml-8 mt-1 px-3 py-2 bg-[#34C759]/5 border border-[#34C759]/30 rounded-lg flex items-center gap-3"
                  data-testid={`practice-runs-row-${cls.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-[#34C759] text-[11px] font-bold uppercase tracking-wider">Number of runs</span>
                  <select
                    value={practiceCounts[cls.id] || 1}
                    onChange={(e) => onSetPracticeCount && onSetPracticeCount(cls.id, parseInt(e.target.value, 10) || 1)}
                    className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#34C759]"
                    data-testid={`practice-runs-select-${cls.id}`}
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {(practiceCounts[cls.id] || 1) > 1 && cls.entry_fee > 0 && (
                    <span className="ml-auto text-[#34C759] text-xs font-bold" data-testid={`practice-runs-total-${cls.id}`}>
                      ${(parseFloat(cls.entry_fee) * (practiceCounts[cls.id] || 1)).toFixed(2)} total
                    </span>
                  )}
                </div>
              )}

              {isSelected && (rolloverMap[cls.id] || []).length > 0 && (
                <div className="ml-8 mt-1 space-y-1" data-testid={`rollovers-for-${cls.id}`}>
                  <p className="text-[#FF9500] text-xs font-semibold flex items-center gap-1 py-1">
                    <ArrowsClockwise size={11} weight="bold" /> Roll over to
                  </p>
                  {availableRollovers.length === 0 && !riderAgeGroup && (rolloverMap[cls.id] || []).some(r => r.rollover_category) && (
                    <p className="text-[#9E9E9E] text-xs py-2 px-3 bg-[#0A0A0A] rounded-lg border border-[#262626]" data-testid="age-group-hint">
                      Select your age group in the Rider Info panel to see eligible rollovers
                    </p>
                  )}
                  {availableRollovers.map((roCls) => {
                    const roSelected = (selectedRollovers[cls.id] || []).includes(roCls.id);
                    return (
                      <div
                        key={roCls.id}
                        onClick={() => onToggleRollover(cls.id, roCls.id)}
                        data-testid={`rollover-select-${roCls.id}`}
                        className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          roSelected
                            ? 'bg-[#FF9500]/10 border-[#FF9500]'
                            : 'bg-[#0A0A0A] border-[#262626] hover:border-[#FF9500]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                              roSelected ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[#555]'
                            }`}>
                              {roSelected && (
                                <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm flex items-center gap-1.5">
                                {roCls.name}
                                {roCls.is_sidepot && (
                                  <span className="inline-block bg-[#FF9500]/20 text-[#FF9500] text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Sidepot</span>
                                )}
                              </p>
                              <p className="text-[#9E9E9E] text-[11px]">
                                {roCls.is_sidepot
                                  ? `Add the ${roCls.name} sidepot to this entry`
                                  : `Rolls your ${cls.name} time into ${roCls.name}`}
                                {roCls.rollover_category && <span className="text-[#FF9500]"> &bull; {roCls.rollover_category} only</span>}
                              </p>
                            </div>
                          </div>
                          <span className="text-[#FF9500] font-bold text-sm" style={{fontFamily: 'JetBrains Mono'}}>+${roCls.entry_fee}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
